import React from "react";
import { Card, Button, Row, Col, Spin, Alert, Select, Statistic, Progress, Table, Tag } from "antd";
import { DownloadOutlined, TeamOutlined, CheckCircleOutlined } from "@ant-design/icons";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "Common/Footer";
import useDepartmentPerformance from "Hooks/AdminHooks/useDepartmentPerformance";
import generateDepartmentPerformancePDF from "FileGenerator/AdminFileGenerator/DepartmentPerformance";
import { message } from "antd";

// Import Chart.js components
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const { Option } = Select;

const DepartmentPerformance = () => {
  const {
    performanceData,
    departments,
    selectedDepartmentId,
    loading,
    fetchingPerformance,
    error,
    handleDepartmentChange,
  } = useDepartmentPerformance();

  const handleDownloadReport = () => {
    try {
      generateDepartmentPerformancePDF(performanceData);
      message.success("Report downloaded successfully!");
    } catch (err) {
      console.error("Error generating PDF:", err);
      message.error("Failed to download report.");
    }
  };

  // Distribution table columns
  const distributionColumns = [
    {
      title: "Grade Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Grade Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
    },
    {
      title: "Grade",
      dataIndex: "grade",
      key: "grade",
      render: (grade) => (
        <Tag
          color={
            grade === "A" ? "green" : grade === "B" ? "blue" : grade === "C" ? "orange" : "red"
          }
        >
          {grade}
        </Tag>
      ),
    },
  ];

  // Data for participation rate pie chart
  const participationChartData = performanceData
    ? {
        labels: ["Reviewed", "Not Reviewed"],
        datasets: [
          {
            data: [
              performanceData.reviewedEmployeeCount,
              performanceData.totalEmployees - performanceData.reviewedEmployeeCount,
            ],
            backgroundColor: ["rgba(24, 144, 255, 0.8)", "rgba(240, 240, 240, 0.8)"],
            borderColor: ["rgba(24, 144, 255, 1)", "rgba(217, 217, 217, 1)"],
            borderWidth: 1,
          },
        ],
      }
    : null;

  // Options for pie chart
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: true,
        text: "Participation Rate",
      },
    },
  };

  // Data for grade distribution bar chart
  const gradeDistributionData =
    performanceData && performanceData.distribution.length > 0
      ? {
          labels: performanceData.distribution.map((item) => item.name),
          datasets: [
            {
              label: "Number of Employees",
              data: performanceData.distribution.map((item) => item.count),
              backgroundColor: [
                "rgba(82, 196, 26, 0.7)", // A - Green
                "rgba(24, 144, 255, 0.7)", // B - Blue
                "rgba(250, 173, 20, 0.7)", // C - Orange
                "rgba(245, 34, 45, 0.7)", // D - Red
              ],
              borderColor: [
                "rgba(82, 196, 26, 1)",
                "rgba(24, 144, 255, 1)",
                "rgba(250, 173, 20, 1)",
                "rgba(245, 34, 45, 1)",
              ],
              borderWidth: 1,
            },
          ],
        }
      : null;

  // Options for bar chart
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "Grade Distribution",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0, // Only show integer values
        },
      },
    },
  };

  return (
    <DashboardLayout>
      <div className="wrapper d-flex flex-column min-vh-100 bg-light">
        <DashboardNavbar />
        <div className="body flex-grow-1 px-3">
          <Card
            title="Department Performance Review"
            className="card-professional"
            extra={
              <Button
                className="btn-primary"
                icon={<DownloadOutlined />}
                onClick={handleDownloadReport}
                disabled={!performanceData || fetchingPerformance}
              >
                Download Report
              </Button>
            }
          >
            {loading && (
              <div style={{ textAlign: "center", padding: "50px" }}>
                <Spin size="large" tip="Loading departments..." />
              </div>
            )}

            {error && (
              <Alert
                message="Error"
                description={error}
                type="error"
                showIcon
                style={{ marginBottom: "20px" }}
              />
            )}

            {!loading && !error && (
              <>
                {/* Department Selector */}
                <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
                  <Col xs={24} md={8}>
                    <label style={{ fontWeight: 500, display: "block", marginBottom: "8px" }}>
                      Select Department:
                    </label>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select a department"
                      value={selectedDepartmentId}
                      onChange={handleDepartmentChange}
                      loading={loading}
                    >
                      {departments.map((dept) => {
                        const deptId = dept.id || dept.departmentId || dept.value;
                        const deptName = dept.name || dept.departmentName || dept.label;

                        return (
                          <Option key={deptId} value={deptId}>
                            {deptName}
                          </Option>
                        );
                      })}
                    </Select>
                  </Col>
                </Row>

                {fetchingPerformance ? (
                  <div style={{ textAlign: "center", padding: "50px" }}>
                    <Spin size="large" tip="Loading performance data..." />
                  </div>
                ) : performanceData ? (
                  <>
                    {/* Department Overview */}
                    <Row gutter={[16, 16]} style={{ marginBottom: "20px" }} className="stats-row">
                      {/* Department */}
                      <Col xs={12} sm={6}>
                        <div className="stats-card-container theme-employees">
                          <div className="stats-icon-wrapper">
                            <TeamOutlined />
                          </div>
                          <div className="stats-info-content">
                            <span className="stats-label-text">Department</span>
                            <span className="stats-value-text">
                              {performanceData.departmentName}
                            </span>
                          </div>
                        </div>
                      </Col>

                      {/* Total Employees */}
                      <Col xs={12} sm={6}>
                        <div className="stats-card-container theme-employees">
                          <div className="stats-icon-wrapper">
                            <TeamOutlined />
                          </div>
                          <div className="stats-info-content">
                            <span className="stats-label-text">Total Employees</span>
                            <span className="stats-value-text">
                              {performanceData.totalEmployees}
                            </span>
                          </div>
                        </div>
                      </Col>

                      {/* Reviewed */}
                      <Col xs={12} sm={6}>
                        <div className="stats-card-container theme-present">
                          <div className="stats-icon-wrapper">
                            <CheckCircleOutlined />
                          </div>
                          <div className="stats-info-content">
                            <span className="stats-label-text">Reviewed</span>
                            <span className="stats-value-text">
                              {performanceData.reviewedEmployeeCount}
                            </span>
                          </div>
                        </div>
                      </Col>

                      {/* Participation Rate */}
                      <Col xs={12} sm={6}>
                        <div className="stats-card-container theme-late">
                          <div className="stats-icon-wrapper">
                            <Progress
                              type="circle"
                              percent={performanceData.participationRate}
                              width={50}
                              strokeColor="var(--primary-main)"
                            />
                          </div>
                          <div className="stats-info-content">
                            <span className="stats-label-text">Participation Rate</span>
                            <span className="stats-value-text">
                              {performanceData.participationRate}%
                            </span>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Charts Section */}
                    <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
                      <Col xs={24} md={12}>
                        <Card title="Participation Overview">
                          {performanceData.totalEmployees > 0 ? (
                            <div style={{ height: "300px", position: "relative" }}>
                              <Pie data={participationChartData} options={pieChartOptions} />
                            </div>
                          ) : (
                            <div
                              style={{
                                height: "300px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <p>No employees in this department</p>
                            </div>
                          )}
                        </Card>
                      </Col>
                      <Col xs={24} md={12}>
                        <Card title="Grade Distribution">
                          {performanceData.distribution.length > 0 ? (
                            <div style={{ height: "300px", position: "relative" }}>
                              <Bar data={gradeDistributionData} options={barChartOptions} />
                            </div>
                          ) : (
                            <div
                              style={{
                                height: "300px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <p>No grade distribution data available</p>
                            </div>
                          )}
                        </Card>
                      </Col>
                    </Row>

      {/* Performance Metrics */}
<Card title="Performance Metrics" style={{ marginBottom: "20px" }}>
  <Row gutter={[16, 16]} justify="center">
   
    {/* Weighted Accountability */}
    <Col xs={24} sm={12} md={8}>
      <div className="stats-card-container theme-present">
        <div className="stats-icon-wrapper">
          <i className="fas fa-balance-scale"></i>
        </div>
        <div className="stats-info-content">
          <span className="stats-label-text">Weighted Accountability</span>
          <span className="stats-value-text">
            {performanceData.avgWeightedAccountability}/50
            <span className="stats-rating-code">
              {(() => {
                const score = performanceData.avgWeightedAccountability || 0;
                const percentage = (score / 50) * 100;
                if (percentage >= 90) return " (FE)";
                if (percentage >= 80) return " (EE)";
                if (percentage >= 70) return " (ME)";
                if (percentage >= 50) return " (PM)";
                return " (FM)";
              })()}
            </span>
          </span>
          <span className="stats-rating-name">
            {(() => {
              const score = performanceData.avgWeightedAccountability || 0;
              const percentage = (score / 50) * 100;
              if (percentage >= 90) return "Far Exceeds Expectations";
              if (percentage >= 80) return "Exceeds Expectations";
              if (percentage >= 70) return "Meets Expectations";
              if (percentage >= 50) return "Partially Meets Expectations";
              return "Fails to Meet Expectations";
            })()}
          </span>
        </div>
      </div>
    </Col>

    {/* Weighted Performance */}
    <Col xs={24} sm={12} md={8}>
      <div className="stats-card-container theme-employees">
        <div className="stats-icon-wrapper">
          <i className="fas fa-trophy"></i>
        </div>
        <div className="stats-info-content">
          <span className="stats-label-text">Weighted Performance Factors</span>
          <span className="stats-value-text">
            {performanceData.avgWeightedPerformance}/40
            <span className="stats-rating-code">
              {(() => {
                const score = performanceData.avgWeightedPerformance || 0;
                const percentage = (score / 40) * 100;
                if (percentage >= 90) return " (FE)";
                if (percentage >= 80) return " (EE)";
                if (percentage >= 70) return " (ME)";
                if (percentage >= 50) return " (PM)";
                return " (FM)";
              })()}
            </span>
          </span>
          <span className="stats-rating-name">
            {(() => {
              const score = performanceData.avgWeightedPerformance || 0;
              const percentage = (score / 40) * 100;
              if (percentage >= 90) return "Far Exceeds Expectations";
              if (percentage >= 80) return "Exceeds Expectations";
              if (percentage >= 70) return "Meets Expectations";
              if (percentage >= 50) return "Partially Meets Expectations";
              return "Fails to Meet Expectations";
            })()}
          </span>
        </div>
      </div>
    </Col>

    {/* Weighted Dynamic Org */}
    <Col xs={24} sm={12} md={8}>
      <div className="stats-card-container theme-leave">
        <div className="stats-icon-wrapper">
          <i className="fas fa-sitemap"></i>
        </div>
        <div className="stats-info-content">
          <span className="stats-label-text">Weighted Dynamic Org</span>
          <span className="stats-value-text">
            {performanceData.avgWeightedDynamicOrg}/10
            <span className="stats-rating-code">
              {(() => {
                const score = performanceData.avgWeightedDynamicOrg || 0;
                const percentage = (score / 10) * 100;
                if (percentage >= 90) return " (FE)";
                if (percentage >= 80) return " (EE)";
                if (percentage >= 70) return " (ME)";
                if (percentage >= 50) return " (PM)";
                return " (FM)";
              })()}
            </span>
          </span>
          <span className="stats-rating-name">
            {(() => {
              const score = performanceData.avgWeightedDynamicOrg || 0;
              const percentage = (score / 10) * 100;
              if (percentage >= 90) return "Far Exceeds Expectations";
              if (percentage >= 80) return "Exceeds Expectations";
              if (percentage >= 70) return "Meets Expectations";
              if (percentage >= 50) return "Partially Meets Expectations";
              return "Fails to Meet Expectations";
            })()}
          </span>
        </div>
      </div>
    </Col>

      {/* Raw Accountability */}
    <Col xs={24} sm={12} md={8}>
      <div className="stats-card-container theme-late">
        <div className="stats-icon-wrapper">
          <i className="fas fa-gavel"></i>
        </div>
        <div className="stats-info-content">
          <span className="stats-label-text">Raw Accountability</span>
          <span className="stats-value-text">
            {performanceData.avgRawAccountability}/5
            <span className="stats-rating-code">
              {(() => {
                const score = performanceData.avgRawAccountability || 0;
                const percentage = (score / 5) * 100;
                if (percentage >= 90) return " (FE)";
                if (percentage >= 80) return " (EE)";
                if (percentage >= 70) return " (ME)";
                if (percentage >= 50) return " (PM)";
                return " (FM)";
              })()}
            </span>
          </span>
          <span className="stats-rating-name">
            {(() => {
              const score = performanceData.avgRawAccountability || 0;
              const percentage = (score / 5) * 100;
              if (percentage >= 90) return "Far Exceeds Expectations";
              if (percentage >= 80) return "Exceeds Expectations";
              if (percentage >= 70) return "Meets Expectations";
              if (percentage >= 50) return "Partially Meets Expectations";
              return "Fails to Meet Expectations";
            })()}
          </span>
        </div>
      </div>
    </Col>



         

    {/* Raw Performance */}
    <Col xs={24} sm={12} md={8}>
      <div className="stats-card-container theme-present">
        <div className="stats-icon-wrapper">
          <i className="fas fa-chart-bar"></i>
        </div>
        <div className="stats-info-content">
          <span className="stats-label-text">Raw Performance Factors</span>
          <span className="stats-value-text">
            {performanceData.avgRawPerformance}/5
            <span className="stats-rating-code">
              {(() => {
                const score = performanceData.avgRawPerformance || 0;
                const percentage = (score / 5) * 100;
                if (percentage >= 90) return " (FE)";
                if (percentage >= 80) return " (EE)";
                if (percentage >= 70) return " (ME)";
                if (percentage >= 50) return " (PM)";
                return " (FM)";
              })()}
            </span>
          </span>
          <span className="stats-rating-name">
            {(() => {
              const score = performanceData.avgRawPerformance || 0;
              const percentage = (score / 5) * 100;
              if (percentage >= 90) return "Far Exceeds Expectations";
              if (percentage >= 80) return "Exceeds Expectations";
              if (percentage >= 70) return "Meets Expectations";
              if (percentage >= 50) return "Partially Meets Expectations";
              return "Fails to Meet Expectations";
            })()}
          </span>
        </div>
      </div>
    </Col>

  
     {/* Raw Dynamic Org */}
    <Col xs={24} sm={12} md={8}>
      <div className="stats-card-container theme-present">
        <div className="stats-icon-wrapper">
          <i className="fas fa-chart-bar"></i>
        </div>
        <div className="stats-info-content">
          <span className="stats-label-text">Raw Dynamic Org</span>
          <span className="stats-value-text">
            {performanceData.avgRawDynamicOrg}/5
            <span className="stats-rating-code">
              {(() => {
                const score = performanceData.avgRawDynamicOrg || 0;
                const percentage = (score / 5) * 100;
                if (percentage >= 90) return " (FE)";
                if (percentage >= 80) return " (EE)";
                if (percentage >= 70) return " (ME)";
                if (percentage >= 50) return " (PM)";
                return " (FM)";
              })()}
            </span>
          </span>
          <span className="stats-rating-name">
            {(() => {
              const score = performanceData.avgRawDynamicOrg || 0;
              const percentage = (score / 5) * 100;
              if (percentage >= 90) return "Far Exceeds Expectations";
              if (percentage >= 80) return "Exceeds Expectations";
              if (percentage >= 70) return "Meets Expectations";
              if (percentage >= 50) return "Partially Meets Expectations";
              return "Fails to Meet Expectations";
            })()}
          </span>
        </div>
      </div>
    </Col>


     {/* Average Total Score */}
    <Col xs={24} sm={12} md={8}>
      <div className="stats-card-container theme-employees">
        <div className="stats-icon-wrapper">
          <i className="fas fa-chart-line"></i>
        </div>
        <div className="stats-info-content">
          <span className="stats-label-text">Average Total Score</span>
          <span className="stats-value-text">
            {performanceData.avgTotalScore}/100
            <span className="stats-rating-code">
              {(() => {
                const score = performanceData.avgTotalScore || 0;
                if (score >= 90) return " (FE)";
                if (score >= 80) return " (EE)";
                if (score >= 70) return " (ME)";
                if (score >= 50) return " (PM)";
                return " (FM)";
              })()}
            </span>
          </span>
          <span className="stats-rating-name">
            {(() => {
              const score = performanceData.avgTotalScore || 0;
              if (score >= 90) return "Far Exceeds Expectations";
              if (score >= 80) return "Exceeds Expectations";
              if (score >= 70) return "Meets Expectations";
              if (score >= 50) return "Partially Meets Expectations";
              return "Fails to Meet Expectations";
            })()}
          </span>
        </div>
      </div>
    </Col>

  </Row>
</Card>

                    {/* Distribution Table */}
                    <Card title="Grade Distribution Details">
                      <Table
                        columns={distributionColumns}
                        dataSource={performanceData.distribution}
                        rowKey="code"
                        pagination={false}
                      />
                    </Card>
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: "50px" }}>
                    <p>Select a department to view performance data</p>
                  </div>
                )}
              </>
            )}
          </Card>
          <Footer />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DepartmentPerformance;
